import * as React from 'react';
import * as styles from './FileUploader.css';
import { Helper } from '../../Helper';

import { IAIcon } from '../icon/Icon';
import { IAMessageBar } from '../messageBar/MessageBar';
import { MAX_FILE_SIZE_BYTES } from '../../global/Constants';
import { LocalizationService } from '../../services/LocalizationService';
import { FetchProxy } from 'intraactive-sdk-helper';
import { sdkContainerInstance } from '../global';

export interface IAFileUploaderFile {
  name?: string;
  url?: string;
  extension?: string;
  status?: "success" | "failed" | "uploading" | "waiting" | "duplicate" | "too big";
  fileRef?: File;
  altText?: string;
}

export interface IAFileUploaderProps {
  token: string;
  uploadUrl: string;
  tenant: string;
  idPicker: string;
  linkColor?: string;
  filesAttached: IAFileUploaderFile[];
  style?: any;
  onChange: (files: IAFileUploaderFile[]) => void;
}

export interface IAFileUploaderState {
  uploadedFiles: IAFileUploaderFile[];
  dragging: boolean;
}

export class IAFileUploader extends React.Component<IAFileUploaderProps, IAFileUploaderState> {

  private localizationService: LocalizationService = new LocalizationService();

  constructor(props: IAFileUploaderProps) {
    super(props);
    this.state = {
      uploadedFiles: [],
      dragging: false
    };
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
  }

  public componentDidMount(): void {
    if (this.props.filesAttached) {
      this.setState({ uploadedFiles: this.props.filesAttached });
    }
  }

  // saves all succesfull uploads on the message
  private setStateAndUpdateMessage(files: IAFileUploaderFile[]): void {
    let filteredFiles = files.filter((file) => {
      return file.status === "success";
    });
    this.setState({ uploadedFiles: files }, () => this.props.onChange(filteredFiles));
  }

  private handleFiles(fileList: FileList): void {
    // first add all files to show the status of the uploads
    var fileUploaderFiles: IAFileUploaderFile[] = [];
    var files: File[] = [];
    for (let i = 0; i < fileList.length; i++) {
      var file = fileList[i];

      // check if file already has been uploaded in this session
      var alreadyUploaded = false;
      this.state.uploadedFiles.forEach(element => {
        if (file.name === element.name) {
          alreadyUploaded = true;
        }
      });

      if (!alreadyUploaded) {
        const lastDot = file.name.lastIndexOf(".")
        const ext = file.name.substring(lastDot);

        fileUploaderFiles.push({ name: file.name, status: "waiting", extension: ext });
        files.push(file);
      }
    }

    let prevLength = this.state.uploadedFiles.length;

    // update state to show initial status of files
    let uploadedFilesUpdated = [...this.state.uploadedFiles, ...fileUploaderFiles];
    this.setState({ uploadedFiles: uploadedFilesUpdated });

    for (let i = 0; i < files.length; i++) {
      // getting ready to update the status of the file
      let uploadedFiles = [...uploadedFilesUpdated];
      let file = uploadedFiles[prevLength + i];
      file.status = "uploading";
      this.setState({ uploadedFiles: uploadedFiles });

      this.uploadSelectedFile(files[i], false)
        .then((fileUrl: string) => {
          console.log(fileUrl);
          file.status = "success";
          file.url = fileUrl;
          file.fileRef = null;
          this.setStateAndUpdateMessage(uploadedFiles);
        })
        .catch((err) => {
          if (err === "too big") {
            file.status = "too big";
          } else {
            file.status = "failed";
          }
          this.setStateAndUpdateMessage(uploadedFiles);
        }
        );
    }
  }

  private uploadSelectedFile(file: File, overwrite: boolean): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      let fileReader = new FileReader();

      fileReader.onload = () => {
        let url = this.props.uploadUrl + `/${this.props.tenant}/uploadfile/${Helper.removeSpecialCharacters(file.name)}/${overwrite}`;
        if (file.size > MAX_FILE_SIZE_BYTES) {
          reject("too big");
        }

        new FetchProxy(url, {
          method: "POST",
          credentials: "same-origin",
          mode: "cors",
          headers: new Headers({
            "AuthToken": this.props.token,
            "Accept": "application/json",
            "Content-Type": "application/json"
          }),
          body: file as Blob
        })
        .withContainer(sdkContainerInstance)
        .fetch()
        .then(function (response) {
          if (!response.ok) {
            throw (response.status);
          } else {
            return response.json();
          }
        }).then((response: { fileUrl: string }) => {
          resolve(response.fileUrl);
        }).catch((error) => {
          error === 413 ? reject("too big") : reject("generic error");
        });
      };
      fileReader.readAsDataURL(file);
    });
  }

  private removeUploadedFile(i: number): void {
    let files = [...this.state.uploadedFiles];
    files.splice(i, 1);
    this.setStateAndUpdateMessage(files);
  }

  public render(): JSX.Element {

    return (
      <div
        style={this.props.style}
      >
        <div
          className={styles.IA_fileUploaderUploadField}
          style={this.state.dragging ? { backgroundColor: "#add8e63b", borderColor: "lightblue" } : {}}
          onDragOver={e => {
            e.preventDefault();
            e.stopPropagation();
            if (e.dataTransfer.items && e.dataTransfer.items.length > 0 && !this.state.dragging) {
              this.setState({ dragging: true });
            }
          }}
          onDragEnter={e => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDragLeave={e => {
            e.preventDefault();
            e.stopPropagation();
            if (this.state.dragging) {
              this.setState({ dragging: false });
            }
          }}
          onDrop={e => {
            e.preventDefault();
            e.stopPropagation();
            this.setState({ dragging: false });
            if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
              this.handleFiles(e.dataTransfer.files);
              e.dataTransfer.clearData();
            }
          }}
        >
          <label className={styles.IA_fileUploaderLabelWrapper}>
            <input
              type="file"
              className={styles.IA_fileUploaderInput}
              id={`fileInput${this.props.idPicker}`}
              multiple
              onChange={() => {
                const fileInput: HTMLInputElement = document.getElementById(`fileInput${this.props.idPicker}`) as HTMLInputElement;
                const fileList = fileInput.files;
                this.handleFiles(fileList);
              }}
            />
            <IAIcon
              title="Attach"
              size={25}
              color={"#666666"}
            />
            <p className={styles.IA_fileUploaderText}>{this.localizationService.strings.FileUploader_ChooseFiles}</p>
          </label>
        </div>

        <div className={styles.IA_fileUploaderFilesContainer}>
          {this.state.uploadedFiles && this.state.uploadedFiles.length > 0 && this.state.uploadedFiles.map((file, i) => (
            <div
              key={i}
            >
              <div
                className={styles.IA_fileUploaderFile}
              >
                {(file.status === "waiting" || file.status === "uploading") &&
                  <div className={styles.IA_fileUploaderSpinner}>
                    <IAIcon
                      title="Spinner"
                      size={20}
                      color={"#666666"}
                    />
                  </div>
                }
                {file.status === "success" &&
                  <IAIcon
                    title="Checkmark"
                    size={22}
                    color={"green"}
                    style={{ marginRight: 10, marginTop: 5 }}
                  />
                }
                {(file.status === "duplicate" || file.status === "failed" || file.status === "too big") &&
                  <IAIcon
                    title="Attention"
                    size={22}
                    color={"red"}
                    style={{ marginRight: 10, marginTop: 5 }}
                  />
                }

                <IAIcon
                  title={Helper.getFileIconFromExtension(file.extension).icon}
                  size={22}
                  color={Helper.getFileIconFromExtension(file.extension).color}
                  style={{ margin: "5px 5px 0 5px" }}
                />

                <div
                  onClick={() => { window.open(file.url, '_blank').focus(); }}
                  style={{
                    color: this.props.linkColor ? this.props.linkColor : "black",
                    cursor: file.url ? "pointer" : "default",
                    width: "calc(100% - 90)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    lineClamp: 1,
                    boxOrient: "vertical"
                  }}
                >
                  {file?.altText && file?.altText?.trim() !== "" ? file?.altText : file?.name}
                </div>

                {(file.status === "success" || file.status === "duplicate" || file.status === "failed" || file.status === "too big") &&
                  <div
                    className={styles.IA_fileUploaderCloseIcon}
                    onClick={() => this.removeUploadedFile(i)}
                  >
                    <IAIcon
                      title="Close"
                      size={20}
                      color={"#666666"}
                    />
                  </div>
                }
              </div>
              {file.status === "duplicate" && (
                <IAMessageBar
                  type="warning"
                  onDismiss={() => {
                    let uploadedFiles = [...this.state.uploadedFiles];
                    let updatedFile = uploadedFiles[i];
                    updatedFile.status = "uploading";
                    this.setState({ uploadedFiles: uploadedFiles });

                    this.uploadSelectedFile(file.fileRef, true)
                      .then((fileUrl: string) => {
                        console.log(fileUrl);
                        file.status = "success";
                        file.url = fileUrl;
                        file.fileRef = null;
                        this.setStateAndUpdateMessage(uploadedFiles);
                      })
                      .catch((err) => {
                        if (err === "too big") {
                          file.status = "too big";
                        } else {
                          file.status = "failed";
                        }
                        this.setStateAndUpdateMessage(uploadedFiles);
                      }
                      );
                  }}
                  content={file.name + this.localizationService.strings.FileUploader_DuplicateFile}
                  closeButtonIcon="Checkmark"
                  showCloseButton={true}
                />
              )}
              {file.status === "too big" && (
                <IAMessageBar
                  type="warning"
                  onDismiss={() => { }}
                  content={this.localizationService.strings.FileUploader_FileTooBig}
                  showCloseButton={false}
                />
              )}
            </div>
          ))
          }
        </div>
      </div>
    );
  }
}